import type { Option } from "@tento-lona/sheets";
import { DateTime, MsSinceEpoch, Utc } from "@tento-chrono";
import { TimeMembership } from "@tento-lona/sheets/internal-sdk";

/** Finds the column whose time range holds a given instant. */
export namespace ColumnTimepointSearch {
  export interface ColumnWithRange {
    range: DateTime.Range<Utc>;
  }

  export interface SearchResult {
    startIndex: number;
    endIndex: number;
  }

  export interface TimepointRange {
    start: MsSinceEpoch;
    end: MsSinceEpoch;
  }

  /**
   * Finds the range of columns that intersect with the given timepoint range
   * using binary search.
   *
   * Columns are assumed to be sorted by their range.start time.
   *
   * A column holds the query range when the range belongs to it under the
   * half-open convention (`TimeMembership.belongsMs`):
   * column.range.start.mse < probeEnd AND column.range.end.mse > queryRange.start,
   * where probeEnd is the range's end — or, for a zero-length range (an
   * instant cell), the next millisecond, so an instant at a column boundary
   * (midnight) finds the column it starts, not an empty span.
   *
   * @param columns Array of columns with time ranges, sorted by range.start
   * @param queryRange The time range to search for
   * @returns Object with startIndex (inclusive) and endIndex (exclusive), or null if no columns intersect
   */
  export function findColumnsInTimepointRange<T extends ColumnWithRange>(
    columns: T[],
    queryRange: TimepointRange,
  ): Option<SearchResult> {
    if (columns.length === 0) {
      return null;
    }

    // Find the leftmost column that might intersect
    // We want the first column where column.range.end.mse > queryRange.start
    const startIndex = findLeftmostIntersecting(columns, queryRange.start);

    if (startIndex === -1) {
      return null;
    }

    // Find the rightmost column that might intersect
    // We want the last column where column.range.start.mse < the probe end
    // (`TimeMembership.probeEnd`: an instant probes as its one-ms span).
    const probeEnd = TimeMembership.probeEnd(queryRange.start, queryRange.end);
    const endIndex = findRightmostIntersecting(columns, probeEnd, startIndex);

    if (endIndex === -1) {
      return null;
    }

    return {
      startIndex,
      endIndex: endIndex + 1, // Make it exclusive for easier iteration
    };
  }

  /**
   * Binary search for the leftmost column where column.range.end.mse > queryStart
   */
  function findLeftmostIntersecting<T extends ColumnWithRange>(
    columns: T[],
    queryStart: MsSinceEpoch,
  ): number {
    let left = 0;
    let right = columns.length - 1;
    let result = -1;

    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const column = columns[mid];
      if (column === undefined) break;

      if (column.range.end.mse > queryStart) {
        result = mid;
        right = mid - 1; // Continue searching left
      } else {
        left = mid + 1;
      }
    }

    return result;
  }

  /**
   * Binary search for the rightmost column where column.range.start.mse < queryEnd
   */
  function findRightmostIntersecting<T extends ColumnWithRange>(
    columns: T[],
    queryEnd: number,
    startFrom: number = 0,
  ): number {
    let left = startFrom;
    let right = columns.length - 1;
    let result = -1;

    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const column = columns[mid];
      if (column === undefined) break;

      if (column.range.start.mse < queryEnd) {
        result = mid;
        left = mid + 1; // Continue searching right
      } else {
        right = mid - 1;
      }
    }

    return result;
  }
}
